/**
 * pricing-filter-track.js
 *
 * Tracks search queries in the pricing/package feature comparison modal.
 * Only fires on explicit user actions: Enter key or click on the search button
 * inside the configured wrapper selector.
 */

import { getCookie, text_truncate, getTrackingNonce } from '../core/utils.js';

export function initPricingFilterTrack() {
  var cfg = (window.mlatConfig && window.mlatConfig.pricingFilter) || {};
  var wrapper = cfg.wrapper || '.mekari-search-term';

  // Build AJAX URL. Prefer the server-baked window.mlatConfig.ajaxUrl over deriving it
  // from <link rel="https://api.w.org/">: on multilingual sites that link can come back
  // locale-prefixed (e.g. /en/wp-json/), and admin-ajax.php is never under that prefix.
  var wpApiLink = document.querySelector('link[rel="https://api.w.org/"]');
  var wpJsonBase = (wpApiLink && wpApiLink.href)
    ? wpApiLink.href.replace(/\/?$/, '/')
    : (window.location.origin + '/wp-json/');
  var wpSiteBase = wpJsonBase.replace(/wp-json\/?$/, '');
  var wpAjaxURL = (window.mlatConfig && window.mlatConfig.ajaxUrl)
    || (wpSiteBase.replace(/\/?$/, '/') + 'wp-admin/admin-ajax.php');

  // Device detection (simple)
  var ua = (navigator.userAgent || '').toLowerCase();
  var device = (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod'))
    ? 'Mobile' : 'Desktop';

  var MIN_LENGTH = 2;

  function sendSearch(term) {
    if (!term || term.length < MIN_LENGTH) return;

    var payload = {
      nonce: getTrackingNonce(),
      search_term: term.substring(0, 50),
      page_url: window.location.href,
      journey: text_truncate(getCookie('user_journey') || '', 250, ''),
      device: device,
      visitor_id: getCookie('visitor_id') || ''
    };

    var gsheetsUrl = cfg.gsheetsUrl || '';

    fetch(wpAjaxURL + '?action=mlat_pricing_filter_store', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
      keepalive: true
    }).then(function (r) { return r.json(); }).then(function (res) {
      if (!res || !res.success) throw new Error('db_fail');
    }).catch(function () {
      if (!gsheetsUrl) return;
      try {
        var fd = new FormData();
        fd.append('Search Term', payload.search_term);
        fd.append('Visitor ID', payload.visitor_id);
        fd.append('Device Type', payload.device);
        fd.append('Page URL', payload.page_url);
        fd.append('Journey', payload.journey);
        fetch(gsheetsUrl, { method: 'POST', body: fd, mode: 'no-cors', keepalive: true }).catch(function () {});
      } catch (e) {}
    });
  }

  function getInputFromWrapper(wrapperEl) {
    return wrapperEl.querySelector('input[type="text"], input[type="search"], input:not([type])');
  }

  function handleKeydown(e) {
    if (e && e.key === 'Enter') {
      var input = e.target;
      if (!input) return;
      var term = (input.value || '').trim();
      sendSearch(term);
    }
  }

  function handleButtonClick(e) {
    var btn = e && e.target;
    if (!btn) return;
    // Walk up to find the wrapper
    var wrapperEl = btn.closest(wrapper);
    if (!wrapperEl) return;
    var input = getInputFromWrapper(wrapperEl);
    if (!input) return;
    var term = (input.value || '').trim();
    sendSearch(term);
  }

  // Bind to all matching wrappers (now + future via MutationObserver)
  function bindWrapper(el) {
    if (!el || el.__mlatPricingFilterBound) return;
    el.__mlatPricingFilterBound = true;

    var input = getInputFromWrapper(el);
    if (input) {
      input.addEventListener('keydown', handleKeydown);
    }

    // Bind click on button(s) inside the wrapper
    var buttons = el.querySelectorAll('button, .btn, [role="button"]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', handleButtonClick);
    }
  }

  function bindAll() {
    try {
      var wrappers = document.querySelectorAll(wrapper);
      for (var i = 0; i < wrappers.length; i++) {
        bindWrapper(wrappers[i]);
      }
    } catch (e) {}
  }

  // Initial bind
  bindAll();

  // Watch for dynamically added wrappers (modals injected later)
  if (typeof MutationObserver === 'function') {
    try {
      var obs = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (!m || !m.addedNodes) continue;
          for (var j = 0; j < m.addedNodes.length; j++) {
            var n = m.addedNodes[j];
            if (!n || n.nodeType !== 1) continue;
            try {
              if (n.matches && n.matches(wrapper)) {
                bindWrapper(n);
              } else if (n.querySelectorAll) {
                var inner = n.querySelectorAll(wrapper);
                for (var k = 0; k < inner.length; k++) {
                  bindWrapper(inner[k]);
                }
              }
            } catch (e) {}
          }
        }
      });
      obs.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }
}
